/**
 * Collection filters with full-catalog indexing via Section Rendering API.
 * Used by sections/basic-collection-filters.liquid
 */
(function () {
  'use strict';

  const BUILTIN_GROUPS = new Set(['vendor', 'product_type', 'type', 'availability', 'size', 'color', 'price']);
  const MOBILE_MEDIA = '(max-width: 999px)';
  const CATALOG_BATCH = 250;
  const FETCH_HANDLE_BATCH = 24;
  const FETCH_CONCURRENCY = 3;
  const MAX_CATALOG_PAGES = 40;

  function handleize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function normalizeTagPrefix(prefix) {
    const raw = String(prefix || '').trim();
    if (!raw) return '';
    const handle = handleize(raw);
    if (!handle) return '';
    return handle.endsWith('-') ? handle : handle + '-';
  }

  function expandTagFilterValue(value) {
    return String(value || '')
      .split('|')
      .map((part) => handleize(part))
      .filter(Boolean);
  }

  function productHasTagHandle(productTags, wantedHandle, tagPrefix) {
    const wanted = handleize(wantedHandle);
    if (!wanted) return false;
    const dynamicPrefix = normalizeTagPrefix(tagPrefix);

    return (productTags || []).some((productTag) => {
      const tag = handleize(productTag);
      if (!tag) return false;
      if (tag === wanted) return true;
      if (dynamicPrefix && tag === dynamicPrefix + wanted) return true;
      if (tag === 'category-' + wanted) return true;
      if (tag === 'gender-' + wanted) return true;
      if (tag === 'product-fits-' + wanted) return true;
      if (tag === 'product-type-' + wanted) return true;
      return false;
    });
  }

  function productMatchesTagValues(productTags, selectedValues, tagPrefix) {
    const wantedHandles = [];
    (selectedValues || []).forEach((rawValue) => {
      expandTagFilterValue(rawValue).forEach((handle) => wantedHandles.push(handle));
    });
    if (!wantedHandles.length) return false;
    return wantedHandles.some((wanted) => productHasTagHandle(productTags, wanted, tagPrefix));
  }

  function parseList(value) {
    return String(value || '')
      .split(',')
      .map(handleize)
      .filter(Boolean);
  }

  function productMatchesColorValues(product, selectedValues, colorGroups) {
    return (selectedValues || []).some((rawValue) =>
      productMatchesColorFilter(product, rawValue, colorGroups)
    );
  }

  function productMatchesSizeValues(productSizes, selectedValues) {
    const productSet = new Set(parseList(productSizes));
    return (selectedValues || []).some((value) => productSet.has(handleize(value)));
  }

  function parseSearchTerms(terms) {
    return String(terms || '')
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  }

  function buildProductSearchHaystack(product) {
    return [
      product && product.handle,
      product && product.vendor,
      product && product.productType,
      product && product.title,
      ...((product && product.tags) || []),
      ...((product && product.colors) || []),
      ...((product && product.sizes) || []),
    ]
      .map((part) => String(part || '').toLowerCase())
      .join(' ');
  }

  function searchTermMatchesHaystack(term, haystack) {
    const normalized = String(term || '').toLowerCase().trim();
    if (!normalized) return true;
    if (haystack.includes(normalized)) return true;
    if (normalized === 'grey' && haystack.includes('gray')) return true;
    if (normalized === 'gray' && haystack.includes('grey')) return true;
    return false;
  }

  function productMatchesSearchTerms(product, terms) {
    if (!terms || !terms.length) return true;
    const haystack = (product && product.searchHaystack) || buildProductSearchHaystack(product);
    return terms.every((term) => searchTermMatchesHaystack(term, haystack));
  }

  function mergeSearchContextParams(params, config) {
    if (!config || !config.searchMode) return params;
    if (config.searchTerms) params.set('q', config.searchTerms);
    params.set('type', config.searchQueryType || 'product');
    params.set('options[prefix]', config.searchQueryPrefix || 'last');
    return params;
  }

  function normalizeColorKey(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/colour/g, 'color')
      .replace(/&/g, ' and ')
      .replace(/\bcolors?\b/g, '')
      .replace(/\band\b/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .trim();
  }

  function colorKeyVariants(raw) {
    const variants = new Set();
    const text = String(raw || '').trim();
    if (!text) return [];

    const normalized = text.toLowerCase().replace(/colour/g, 'color').replace(/&/g, ' and ');
    const core = normalized
      .replace(/ colors/g, '')
      .replace(/ color/g, '')
      .replace(/ colours/g, '')
      .replace(/ colour/g, '');

    [
      text,
      normalized,
      core,
      handleize(text),
      handleize(normalized),
      handleize(core),
      normalizeColorKey(text),
      normalizeColorKey(handleize(text)),
      normalizeColorKey(normalized),
      normalizeColorKey(handleize(normalized)),
      normalizeColorKey(core),
      normalizeColorKey(handleize(core)),
    ].forEach((key) => {
      if (key) variants.add(key);
    });

    return [...variants];
  }

  function expandColorFilterValue(value) {
    const keys = new Set();
    String(value || '')
      .split('|')
      .forEach((part) => {
        colorKeyVariants(part).forEach((key) => keys.add(key));
      });
    return [...keys];
  }

  function buildKnownColorKeys(colorGroups) {
    const keys = new Set();
    (colorGroups || []).forEach((group) => {
      expandColorFilterValue(group.value).forEach((key) => keys.add(key));
      colorKeyVariants(group.label).forEach((key) => keys.add(key));
      colorKeyVariants(group.labelHandle).forEach((key) => keys.add(key));
      (group.tagHandles || []).forEach((tag) => {
        colorKeyVariants(tag).forEach((key) => keys.add(key));
        keys.add(handleize(tag));
      });
    });
    return keys;
  }

  function tagIsColorTag(tag, knownColorKeys) {
    if (!knownColorKeys || knownColorKeys.size === 0) return true;
    if (knownColorKeys.has(tag)) return true;
    if (knownColorKeys.has(handleize(tag))) return true;
    if (knownColorKeys.has(normalizeColorKey(tag))) return true;
    return colorKeyVariants(tag).some((key) => knownColorKeys.has(key));
  }

  function addTagColorKeys(tag, keys) {
    const raw = String(tag || '').trim();
    if (!raw || !keys) return;

    colorKeyVariants(raw).forEach((key) => keys.add(key));
    keys.add(handleize(raw));
    keys.add(normalizeColorKey(raw));

    const stripped = raw.replace(/^(color|colour|colors|colours)-/i, '');
    if (stripped && stripped !== raw) {
      colorKeyVariants(stripped).forEach((key) => keys.add(key));
      keys.add(handleize(stripped));
      keys.add(normalizeColorKey(stripped));
    }
  }

  function computeProductColorKeySet(product, knownColorKeys) {
    const keys = new Set();
    (product?.colors || []).forEach((color) => {
      colorKeyVariants(color).forEach((key) => keys.add(key));
    });
    (product?.tags || []).forEach((tag) => {
      if (tagIsColorTag(tag, knownColorKeys)) {
        addTagColorKeys(tag, keys);
      }
    });
    return keys;
  }

  function productMatchesColorFilter(product, filterValue, colorGroups) {
    const wanted = expandColorFilterValue(filterValue);
    if (!wanted.length) return false;

    const productKeys = (product && product.colorKeySet) || new Set();
    if (wanted.some((key) => productKeys.has(key))) return true;

    const group = (colorGroups || []).find((item) => item.value === filterValue);
    if (!group) return false;

    const groupKeys = new Set(wanted);
    expandColorFilterValue(group.label).forEach((key) => groupKeys.add(key));
    colorKeyVariants(group.labelHandle).forEach((key) => groupKeys.add(key));
    (group.tagHandles || []).forEach((tag) => {
      colorKeyVariants(tag).forEach((key) => groupKeys.add(key));
      groupKeys.add(handleize(tag));
    });

    return (product.tags || []).some((tag) =>
      colorKeyVariants(tag).some((key) => groupKeys.has(key))
    );
  }

  function getProductCardData(item) {
    const card = item.querySelector('.collection-product-card') || item;
    return {
      handle: item.dataset.productHandle || card.dataset.productHandle || '',
      vendor: card.dataset.vendor || '',
      productType: card.dataset.productType || card.dataset.type || '',
      availability: card.dataset.availability || 'in-stock',
      tags: parseList(card.dataset.tags),
      colors: parseList(card.dataset.colors),
      sizes: parseList(card.dataset.sizes),
      price: parseFloat(card.dataset.price) || 0,
    };
  }

  function parseCatalogPayload(raw) {
    if (raw && typeof raw === 'object') {
      return {
        page: parseInt(raw.p ?? raw.page, 10) || 1,
        pages: parseInt(raw.n ?? raw.pages, 10) || 1,
        products: Array.isArray(raw.d) ? raw.d : Array.isArray(raw.products) ? raw.products : [],
      };
    }

    const text = String(raw || '').trim();
    if (!text) return { page: 1, pages: 1, products: [] };

    if (text.startsWith('{')) {
      try {
        return parseCatalogPayload(JSON.parse(text));
      } catch (error) {
        /* fall through */
      }
    }

    const match = text.match(/\{"p"\s*:\s*\d+\s*,\s*"n"\s*:\s*\d+\s*,\s*"d"\s*:\s*\[[\s\S]*?\]\s*\}/);
    if (match) {
      try {
        return parseCatalogPayload(JSON.parse(match[0]));
      } catch (error) {
        /* fall through */
      }
    }

    return { page: 1, pages: 1, products: [] };
  }

  function parseJsonProduct(raw) {
    const tags = (Array.isArray(raw.tags) ? raw.tags : String(raw.tags || '').split(','))
      .map((tag) => handleize(String(tag).trim()))
      .filter(Boolean);
    const colors = [];
    const sizes = [];
    const seenColors = new Set();
    const seenSizes = new Set();
    let minPrice = Infinity;

    (raw.variants || []).forEach((variant) => {
      const price = parseFloat(variant.price);
      if (!Number.isNaN(price) && price < minPrice) minPrice = price;
    });

    (raw.options || []).forEach((option) => {
      const name = String(option.name || '').toLowerCase();
      (option.values || []).forEach((value) => {
        const key = handleize(value);
        if (name === 'color' || name === 'colour' || name === 'colours' || name.includes('color')) {
          if (key && !seenColors.has(key)) {
            seenColors.add(key);
            colors.push(key);
          }
        } else if (name.includes('size')) {
          if (key && key !== 'default-title' && !seenSizes.has(key)) {
            seenSizes.add(key);
            sizes.push(key);
          }
        }
      });
    });

    const available = raw.available === true || (raw.variants || []).some((variant) => variant.available);

    return {
      handle: raw.handle || '',
      vendor: handleize(raw.vendor || ''),
      productType: handleize(raw.product_type || ''),
      availability: available ? 'in-stock' : 'out-of-stock',
      tags,
      colors,
      sizes,
      price: minPrice === Infinity ? 0 : minPrice / 100,
    };
  }

  function formatLabel(handle) {
    return String(handle || '')
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value || ''));
    }
    return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function minifyCachedCardHtml(html) {
    return String(html || '')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/>\s+</g, '><')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function stripCardHtml(html) {
    if (!html) return '';
    const cleaned = String(html)
      .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
      .trim();

    try {
      const doc = new DOMParser().parseFromString(cleaned, 'text/html');
      const items = doc.querySelectorAll('li[data-product-handle]');
      if (items.length) {
        return [...items].map((li) => li.outerHTML).join('');
      }
    } catch (error) {
      /* fall through */
    }

    const pattern = /<li\b[^>]*data-product-handle="[^"]+"[\s\S]*?<\/li>/gi;
    const parts = cleaned.match(pattern);
    return parts ? parts.join('') : '';
  }

  function normalizeGridItem(li) {
    if (!li) return;
    li.classList.remove('scroll-trigger', 'animate--slide-in', 'basic-filter-hidden');
    li.removeAttribute('data-cascade');
    li.style.removeProperty('--animation-order');
    li.hidden = false;
    li.style.display = '';
  }

  function reorderGridToHandles(grid, orderedHandles, gridItems) {
    if (!grid || !orderedHandles || !orderedHandles.length) return;
    const frag = document.createDocumentFragment();
    orderedHandles.forEach((handle) => {
      const li = gridItems && gridItems.get(handle)
        ? gridItems.get(handle)
        : grid.querySelector(`li[data-product-handle="${escapeSelector(handle)}"]`);
      if (li) frag.appendChild(li);
    });
    grid.appendChild(frag);
  }

  function appendProductCards(grid, html, gridItems) {
    if (!grid || !html) return 0;

    const temp = document.createElement('div');
    temp.innerHTML = html;
    let appended = 0;

    temp.querySelectorAll('li[data-product-handle]').forEach((li) => {
      const handle = li.getAttribute('data-product-handle');
      if (!handle) return;
      if (gridItems && gridItems.has(handle)) return;
      if (!gridItems && grid.querySelector(`li[data-product-handle="${escapeSelector(handle)}"]`)) return;
      normalizeGridItem(li);
      grid.appendChild(li);
      if (gridItems) gridItems.set(handle, li);
      appended += 1;
    });

    return appended;
  }

  async function runPool(items, concurrency, worker) {
    const results = new Array(items.length);
    let index = 0;

    async function runWorker() {
      while (index < items.length) {
        const current = index;
        index += 1;
        results[current] = await worker(items[current], current);
      }
    }

    const workers = [];
    const limit = Math.min(concurrency, items.length);
    for (let i = 0; i < limit; i += 1) {
      workers.push(runWorker());
    }
    await Promise.all(workers);
    return results;
  }

  class BasicCollectionFilters {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-basic-grid]');
      this.form = root.querySelector('[data-basic-filters]');
      this.tagsEl = root.querySelector('[data-basic-active-filters]');
      this.countEls = [...root.querySelectorAll('[data-basic-count], [data-basic-count-footer]')];
      this.emptyEl = root.querySelector('[data-basic-empty]');
      this.paginationEl = root.querySelector('.pagination');
      this.items = this.grid ? [...this.grid.querySelectorAll('li[data-product-handle]')] : [];
      this.productIndex = [];
      this.catalogByHandle = new Map();
      this.cardHtmlCache = new Map();
      this.gridItems = new Map();
      this.preservedCardStyles = '';
      this.catalogLoaded = false;
      this.catalogPromise = null;
      this.filtered = false;
      this._applying = false;
      this.tagPrefixByGroup = this.buildTagPrefixMap();
      this.priceRange = null;
      this.config = this.parseConfig();
      this.initialGridHtml = this.grid ? this.grid.innerHTML : '';

      if (!this.grid || !this.form) return;

      this.cacheInitialCards();
      this.buildGridIndex();
      this.preservedCardStyles = this.extractCardStylesFromGrid();
      this.rebuildProductIndex();

      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[data-basic-price-input]')) return;
        this.syncTagBoxState();
        void this.apply();
      });
      this.form.addEventListener('click', (event) => {
        const clearBtn = event.target.closest('[data-basic-clear]');
        if (clearBtn) {
          event.preventDefault();
          this.clear();
          return;
        }
        const applyPriceBtn = event.target.closest('[data-basic-apply-price]');
        if (applyPriceBtn) {
          event.preventDefault();
          this.readPriceRange();
          void this.apply();
        }
      });

      this.bindCollapsible();
      this.bindMobileFilters();
      this.bindViewSwitcher();
      this.bindSortDropdown();
      void this.bootstrap();
    }

    async bootstrap() {
      this.syncTagBoxState();
      this.ingestEmbeddedCatalog();
      this.mergeDomProductData();
      this.rebuildProductIndex();
      await this.loadFullCatalog();
      this.mergeDomProductData();
      this.rebuildProductIndexFromCatalog();
      this.syncFacetCounts();
      await this.apply();
    }

    getCollectionBasePath() {
      const raw = (this.config && this.config.collectionUrl) || window.location.pathname;
      try {
        return new URL(raw, window.location.origin).pathname.replace(/\/$/, '');
      } catch (error) {
        return String(raw).split('?')[0].replace(/\/$/, '');
      }
    }

    buildSectionApiUrl(extraParams) {
      const url = new URL(this.getCollectionBasePath(), window.location.origin);
      const params = new URLSearchParams();
      if (this.config && this.config.sectionApiId) {
        params.set('sections', this.config.sectionApiId);
      }
      mergeSearchContextParams(params, this.config);
      const sort = new URLSearchParams(window.location.search).get('sort_by');
      if (sort) params.set('sort_by', sort);
      if (extraParams) {
        Object.entries(extraParams).forEach(([key, value]) => {
          if (value != null && value !== '') params.set(key, String(value));
        });
      }
      url.search = params.toString();
      return url;
    }

    getSearchTerms() {
      if (!this.config || !this.config.searchMode) return [];
      const raw =
        this.config.searchTerms || new URLSearchParams(window.location.search).get('q') || '';
      return parseSearchTerms(raw);
    }

    applySearchTermScope() {
      if (!this.config || !this.config.searchMode) return;
      const terms = this.getSearchTerms();
      if (!terms.length) return;

      [...this.catalogByHandle.entries()].forEach(([handle, product]) => {
        if (!productMatchesSearchTerms(product, terms)) {
          this.catalogByHandle.delete(handle);
        }
      });
    }

    updateSearchResultsHeading(count) {
      if (!this.config || !this.config.searchMode) return;
      const total = Number.isFinite(count) ? count : this.productIndex.length;
      const terms = this.config.searchTerms || '';
      const title = this.root.querySelector('[data-basic-page-title]');
      if (title && terms) {
        title.textContent =
          total === 1
            ? `1 result found for “${terms}”`
            : `${total} results found for “${terms}”`;
      }
    }

    ingestEmbeddedCatalog() {
      this.root.querySelectorAll('[data-basic-col-catalog]').forEach((el) => {
        try {
          const payload = parseCatalogPayload(JSON.parse(el.textContent || '{}'));
          this.ingestCatalogProducts(payload.products);
        } catch (error) {
          /* ignore malformed chunk */
        }
      });
    }

    mergeDomProductData() {
      this.items.forEach((item) => {
        const data = getProductCardData(item);
        if (!data.handle) return;
        const existing = this.catalogByHandle.get(data.handle);
        if (!existing) {
          this.mergeCatalogProduct(data);
          return;
        }
        this.mergeCatalogProduct({
          ...existing,
          ...data,
          tags: data.tags.length ? data.tags : existing.tags,
          colors: data.colors.length ? data.colors : existing.colors,
          sizes: data.sizes.length ? data.sizes : existing.sizes,
        });
      });
    }

    parseConfig() {
      const el = this.root.querySelector('[data-basic-col-config]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent || '{}');
      } catch (error) {
        return null;
      }
    }

    getKnownColorKeys() {
      if (!this._knownColorKeys) {
        this._knownColorKeys = buildKnownColorKeys(this.config && this.config.colorGroups);
      }
      return this._knownColorKeys;
    }

    finalizeCatalogProduct(product) {
      if (!product) return product;
      product.colorKeySet = computeProductColorKeySet(product, this.getKnownColorKeys());
      if (this.config && this.config.searchMode) {
        product.searchHaystack = buildProductSearchHaystack(product);
      }
      return product;
    }

    mergeCatalogProduct(product) {
      if (!product || !product.handle) return;
      const existing = this.catalogByHandle.get(product.handle);
      const merged = existing
        ? {
            ...product,
            vendor: product.vendor || existing.vendor,
            productType: product.productType || existing.productType,
            tags: product.tags && product.tags.length ? product.tags : existing.tags,
            colors: product.colors && product.colors.length ? product.colors : existing.colors,
            sizes: product.sizes && product.sizes.length ? product.sizes : existing.sizes,
            price: product.price || existing.price,
            availability: product.availability || existing.availability,
          }
        : product;
      this.finalizeCatalogProduct(merged);
      this.catalogByHandle.set(merged.handle, merged);
    }

    parseCatalogEntry(raw) {
      const tags = Array.isArray(raw.g) ? raw.g.map((tag) => handleize(tag)) : [];
      const colors = Array.isArray(raw.c) ? raw.c.map((color) => handleize(color)) : [];
      const sizes = Array.isArray(raw.s) ? raw.s.map((size) => handleize(size)) : [];
      return {
        handle: raw.h || raw.handle || '',
        vendor: handleize(raw.v ?? raw.vendor),
        productType: handleize(raw.t ?? raw.type),
        availability: raw.a === 0 || raw.a === '0' ? 'out-of-stock' : 'in-stock',
        tags,
        colors,
        sizes,
        price: parseFloat(raw.p ?? raw.price) || 0,
      };
    }

    cacheInitialCards() {
      this.items.forEach((item) => {
        const handle = item.dataset.productHandle;
        if (handle && !this.cardHtmlCache.has(handle)) {
          this.cardHtmlCache.set(handle, minifyCachedCardHtml(item.outerHTML));
        }
      });
    }

    buildGridIndex() {
      this.gridItems = new Map();
      if (!this.grid) return;
      this.grid.querySelectorAll('li[data-product-handle]').forEach((li) => {
        const handle = li.getAttribute('data-product-handle');
        if (handle) this.gridItems.set(handle, li);
      });
    }

    extractCardStylesFromGrid() {
      if (!this.grid) return '';
      const style = this.grid.querySelector('li style');
      return style ? style.outerHTML : '';
    }

    ensureCardStyles() {
      if (!this.preservedCardStyles || !this.grid) return;
      if (this.grid.querySelector('li style')) return;

      const parent = this.grid.parentElement;
      if (!parent) return;

      let holder = parent.querySelector('[data-basic-card-styles]');
      if (!holder) {
        holder = document.createElement('div');
        holder.hidden = true;
        holder.setAttribute('data-basic-card-styles', '');
        parent.insertBefore(holder, this.grid);
      }
      holder.innerHTML = this.preservedCardStyles;
    }

    setItemVisible(li, visible) {
      li.classList.toggle('basic-filter-hidden', !visible);
      li.style.display = visible ? '' : 'none';
    }

    syncGridUi() {
      window.requestAnimationFrame(() => {
        if (window.bindCollectionProductCardInteractions) {
          window.bindCollectionProductCardInteractions(this.grid);
        } else if (window.initCollectionProductCardImages) {
          window.initCollectionProductCardImages(this.grid);
        }
      });
    }

    needsFullCatalog() {
      if (this.config && this.config.searchMode) {
        if (this.config.searchHasNextPage) return true;
        const expected = parseInt(this.config.collectionProductCount, 10);
        if (!Number.isFinite(expected) || expected <= 0) return true;
        return this.catalogByHandle.size < expected;
      }
      const expected = parseInt(this.config && this.config.collectionProductCount, 10);
      if (!Number.isFinite(expected) || expected <= 0) return true;
      return this.catalogByHandle.size < expected;
    }

    async fetchCatalogPage(page) {
      if (!this.config || !this.config.sectionApiId) {
        throw new Error('missing section config');
      }

      const url = this.buildSectionApiUrl({
        basic_col_catalog: '1',
        page: String(page),
      });

      const res = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) throw new Error('catalog section ' + res.status);
      const data = await res.json();
      const raw = data[this.config.sectionApiId] || Object.values(data)[0] || '{}';
      return parseCatalogPayload(raw);
    }

    async fetchProductsJsonPage(page) {
      const url = new URL(this.getCollectionBasePath() + '/products.json', window.location.origin);
      url.searchParams.set('limit', String(CATALOG_BATCH));
      url.searchParams.set('page', String(page));

      const res = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) throw new Error('products.json ' + res.status);
      const data = await res.json();
      return (data.products || []).map((raw) => parseJsonProduct(raw));
    }

    async loadCatalogFromSectionApi() {
      const first = await this.fetchCatalogPage(1);
      const totalPages = Math.min(MAX_CATALOG_PAGES, Math.max(1, first.pages || 1));
      const pageResults = [first];

      if (totalPages > 1) {
        const pages = [];
        for (let page = 2; page <= totalPages; page += 1) {
          pages.push(page);
        }
        const rest = await runPool(pages, FETCH_CONCURRENCY, (page) => this.fetchCatalogPage(page));
        pageResults.push(...rest);
      }

      pageResults.forEach((result) => this.ingestCatalogProducts(result.products));
      this.applySearchTermScope();
      return this.catalogByHandle.size;
    }

    async loadCatalogFromProductsJson() {
      for (let page = 1; page <= MAX_CATALOG_PAGES; page += 1) {
        const products = await this.fetchProductsJsonPage(page);
        if (!products.length) break;
        products.forEach((product) => this.mergeCatalogProduct(product));
        if (products.length < CATALOG_BATCH) break;
      }

      return this.catalogByHandle.size;
    }

    ingestCatalogProducts(products) {
      (products || []).forEach((raw) => {
        const product = this.parseCatalogEntry(raw);
        this.mergeCatalogProduct(product);
      });
    }

    async loadFullCatalog() {
      if (this.catalogLoaded) return;
      if (this.catalogPromise) return this.catalogPromise;

      this.catalogPromise = (async () => {
        let loadedCount = this.catalogByHandle.size;

        if (this.needsFullCatalog()) {
          try {
            await this.loadCatalogFromSectionApi();
          } catch (error) {
            console.warn('[BasicCollectionFilters] section catalog failed, using products.json', error);
          }

          if (!(this.config && this.config.searchMode)) {
            try {
              await this.loadCatalogFromProductsJson();
            } catch (error) {
              console.warn('[BasicCollectionFilters] products.json catalog failed', error);
            }
          }
        }

        this.applySearchTermScope();
        this.rebuildProductIndexFromCatalog();

        this.catalogLoaded = true;
        loadedCount = this.catalogByHandle.size;
        this.root.classList.toggle('basic-col-catalog-synced', loadedCount > 0);
        this.root.dataset.basicCatalogCount = String(loadedCount);
      })();

      return this.catalogPromise;
    }

    async ensureCatalogLoaded() {
      if (this.catalogLoaded) return;
      await this.loadFullCatalog();
    }

    rebuildProductIndexFromCatalog() {
      if (!this.catalogByHandle.size) return;
      this.productIndex = [...this.catalogByHandle.values()].map((product) =>
        this.finalizeCatalogProduct(product)
      );
    }

    buildTagPrefixMap() {
      const map = new Map();
      if (!this.form) return map;
      this.form.querySelectorAll('.filter-options[data-tag-prefix]').forEach((group) => {
        const key = group.dataset.filterGroup || group.dataset.navColFilter;
        if (key) map.set(key, group.dataset.tagPrefix || '');
      });
      return map;
    }

    readPriceRange() {
      const minEl = this.form.querySelector('input[name="price_min"]');
      const maxEl = this.form.querySelector('input[name="price_max"]');
      const min = minEl && minEl.value !== '' ? parseFloat(minEl.value) : 0;
      const max = maxEl && maxEl.value !== '' ? parseFloat(maxEl.value) : Infinity;
      if (min > 0 || max < Infinity) {
        this.priceRange = { min, max };
      } else {
        this.priceRange = null;
      }
    }

    bindCollapsible() {
      this.root.querySelectorAll('.filter-group--collapsible .filter-group-title--toggle').forEach((toggle) => {
        const group = toggle.closest('.filter-group--collapsible');
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          if (group) group.classList.toggle('is-collapsed', expanded);
        });
      });
    }

    bindMobileFilters() {
      const collectionSmart = this.root.querySelector('.collection_smart');
      const sidebar = this.root.querySelector('.filter-sidebar');
      const mobileTab = this.root.querySelector('[data-basic-mobile-filters]');
      const desktopTab = this.root.querySelector('[data-basic-desktop-filters]');
      const closeBtn = this.root.querySelector('.mobile-filters-close');
      const backdrop = this.root.querySelector('.mobile-filters-backdrop');

      if (!sidebar || !backdrop) return;

      const setOpen = (isOpen) => {
        sidebar.classList.toggle('is-open', isOpen);
        backdrop.hidden = !isOpen;
        if (mobileTab) {
          mobileTab.classList.toggle('is-open', isOpen);
          mobileTab.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
        if (desktopTab) {
          desktopTab.classList.toggle('is-open', isOpen);
          desktopTab.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
        if (collectionSmart && collectionSmart.classList.contains('collection_smart--filters-top')) {
          collectionSmart.classList.toggle('is-open-desktop', isOpen);
        }
        document.body.style.overflow = isOpen && window.matchMedia(MOBILE_MEDIA).matches ? 'hidden' : '';
      };

      const toggle = () => setOpen(!sidebar.classList.contains('is-open'));

      mobileTab?.addEventListener('click', toggle);
      desktopTab?.addEventListener('click', toggle);
      closeBtn?.addEventListener('click', () => setOpen(false));
      backdrop.addEventListener('click', () => setOpen(false));

      window.matchMedia(MOBILE_MEDIA).addEventListener('change', (event) => {
        if (!event.matches) setOpen(false);
      });
    }

    bindViewSwitcher() {
      const switcher = this.root.querySelector('[data-basic-view-switcher]');
      if (!switcher) return;

      const storageKey = 'basic-collection-columns-' + (this.root.dataset.sectionId || 'default');
      const saved = localStorage.getItem(storageKey);
      if (saved) this.root.style.setProperty('--basic-desktop-columns', saved);

      switcher.querySelectorAll('[data-columns]').forEach((button) => {
        button.addEventListener('click', () => {
          const value = button.dataset.columns;
          if (!value) return;
          this.root.style.setProperty('--basic-desktop-columns', value);
          localStorage.setItem(storageKey, value);
          switcher.querySelectorAll('[data-columns]').forEach((btn) => {
            const active = btn.dataset.columns === value;
            btn.classList.toggle('is-active', active);
            btn.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
        });
      });
    }

    bindSortDropdown() {
      const sortDropdown = this.root.querySelector('.collection-top-bar__right .link-dropdown');
      if (!sortDropdown) return;

      const sortDropdownButton = sortDropdown.querySelector('.link-dropdown__button');
      const sortDropdownOptions = sortDropdown.querySelector('.link-dropdown__options');
      if (!sortDropdownButton || !sortDropdownOptions) return;

      const closeSortDropdown = () => {
        sortDropdownButton.setAttribute('aria-expanded', 'false');
      };

      sortDropdownButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const isOpen = sortDropdownButton.getAttribute('aria-expanded') === 'true';
        sortDropdownButton.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });

      document.addEventListener('click', (event) => {
        if (!sortDropdown.contains(event.target)) {
          closeSortDropdown();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeSortDropdown();
        }
      });
    }

    syncTagBoxState() {
      this.form
        .querySelectorAll('.filter-option--tag-box, .filter-option--color-swatch')
        .forEach((option) => {
          const input = option.querySelector('.filter-checkbox');
          option.classList.toggle('is-selected', !!(input && input.checked));
        });
    }

    getActive() {
      const groups = {};
      this.form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        const key = input.dataset.filterGroup || input.name;
        if (!key || key === 'price_min' || key === 'price_max') return;
        if (!groups[key]) groups[key] = new Set();
        if (input.checked) groups[key].add(input.value);
      });
      return groups;
    }

    hasActiveFilters(active) {
      const groups = active || this.getActive();
      if (this.priceRange) return true;
      return Object.keys(groups).some((key) => groups[key] && groups[key].size > 0);
    }

    itemMatches(item, active) {
      return this.productDataMatches(getProductCardData(item), active, this.priceRange);
    }

    productDataMatches(data, active, priceRange) {
      const { vendor, productType, availability, tags, colors, sizes, price } = data;
      const checks = [];

      if (active.vendor && active.vendor.size) {
        checks.push(() => active.vendor.has(vendor));
      }
      const typeGroup = active.product_type || active.type;
      if (typeGroup && typeGroup.size) {
        checks.push(() => typeGroup.has(productType));
      }
      if (active.availability && active.availability.size) {
        checks.push(() => active.availability.has(availability));
      }
      if (active.size && active.size.size) {
        checks.push(() => productMatchesSizeValues(sizes, [...active.size]));
      }
      if (active.color && active.color.size) {
        const colorGroups = (this.config && this.config.colorGroups) || [];
        checks.push(() => productMatchesColorValues(data, [...active.color], colorGroups));
      }
      if (priceRange) {
        checks.push(() => price >= priceRange.min && price <= priceRange.max);
      }

      Object.keys(active).forEach((key) => {
        if (BUILTIN_GROUPS.has(key) || !active[key].size) return;
        const tagPrefix = this.tagPrefixByGroup.get(key) || '';
        checks.push(() => productMatchesTagValues(tags, [...active[key]], tagPrefix));
      });

      if (!checks.length) return true;
      return checks.every((check) => check());
    }

    getActiveExcluding(excludeKey) {
      const active = this.getActive();
      const next = {};
      Object.keys(active).forEach((key) => {
        if (key === excludeKey) return;
        next[key] = active[key];
      });
      return next;
    }

    getPoolExcluding(excludeKey) {
      const active = this.getActiveExcluding(excludeKey);
      const priceRange = excludeKey === 'price' ? null : this.priceRange;
      return this.productIndex.filter((product) => this.productDataMatches(product, active, priceRange));
    }

    getMatchingHandles(active) {
      const priceRange = this.priceRange;
      return this.productIndex
        .filter((product) => this.productDataMatches(product, active, priceRange))
        .map((product) => product.handle)
        .filter(Boolean);
    }

    getLabelBase(option, input) {
      const label = option?.querySelector('.filter-label');
      if (label?.dataset.filterLabelBase) return label.dataset.filterLabelBase.trim();
      const textEl = option?.querySelector('.filter-label-text');
      if (textEl) return textEl.textContent.trim();
      if (label) return label.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      return handleize(input?.value || '');
    }

    setFilterOptionVisible(option, visible) {
      if (!option) return;
      option.hidden = !visible;
      option.classList.toggle('is-zero-count', !visible);
      option.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) {
        option.style.removeProperty('display');
      } else {
        option.style.setProperty('display', 'none', 'important');
      }
    }

    updateOptionCount(input, count) {
      const option = input?.closest('.filter-option');
      if (!option) return;

      const visible = count > 0;
      if (!visible && input.checked) input.checked = false;
      input.disabled = !visible;
      this.setFilterOptionVisible(option, visible);
      option.classList.toggle('is-zero-count', !visible);

      const isColorSwatch = option.classList.contains('filter-option--color-swatch');
      if (isColorSwatch) return;

      const base = this.getLabelBase(option, input);
      const label = option.querySelector('.filter-label');
      if (label) label.dataset.filterLabelBase = base;

      const countEl = option.querySelector('.filter-label-count');
      if (countEl) {
        countEl.textContent = `(${count})`;
        return;
      }

      const filterCountEl = option.querySelector('.filter-count');
      if (filterCountEl) {
        filterCountEl.textContent = `(${count})`;
        return;
      }

      if (label && option.classList.contains('filter-option--tag-box')) {
        label.textContent = `${base} (${count})`;
      }
    }

    syncCheckboxGroupCounts(groupKey, matchFn) {
      const pool = this.getPoolExcluding(groupKey);
      const selector = `[data-filter-group="${groupKey}"], [data-nav-col-filter="${groupKey}"]`;
      const container = this.form.querySelector(selector);
      if (!container) return;

      container.querySelectorAll('input.filter-checkbox').forEach((input) => {
        let count = 0;
        pool.forEach((product) => {
          if (matchFn(product, input.value)) count += 1;
        });
        this.updateOptionCount(input, count);
      });
    }

    syncFacetCounts() {
      if (!this.form) return;

      if (this.productIndex.length) {
        this.syncCheckboxGroupCounts('vendor', (product, value) => product.vendor === handleize(value));
        this.syncCheckboxGroupCounts('product_type', (product, value) => product.productType === handleize(value));
        this.syncCheckboxGroupCounts('availability', (product, value) => product.availability === value);
        this.syncCheckboxGroupCounts('size', (product, value) => productMatchesSizeValues(product.sizes, [value]));

        const colorPool = this.getPoolExcluding('color');
        const colorGroups = (this.config && this.config.colorGroups) || [];
        this.form.querySelectorAll('[data-filter-group="color"] .filter-checkbox, [data-nav-col-filter="color"] .filter-checkbox').forEach((input) => {
          let count = 0;
          colorPool.forEach((product) => {
            if (productMatchesColorValues(product, [input.value], colorGroups)) count += 1;
          });
          this.updateOptionCount(input, count);
        });

        this.form.querySelectorAll('.filter-options[data-filter-group^="tag_"], .filter-options[data-nav-col-filter^="tag_"]').forEach((container) => {
          const groupKey = container.dataset.filterGroup || container.dataset.navColFilter;
          if (!groupKey) return;
          const tagPrefix = container.dataset.tagPrefix || this.tagPrefixByGroup.get(groupKey) || '';
          const pool = this.getPoolExcluding(groupKey);

          container.querySelectorAll('input.filter-checkbox').forEach((input) => {
            let count = 0;
            pool.forEach((product) => {
              if (productMatchesTagValues(product.tags, [input.value], tagPrefix)) count += 1;
            });
            this.updateOptionCount(input, count);
          });
        });

        this.syncTagBoxState();
      }

      this.syncGroupVisibility();
    }

    syncGroupVisibility() {
      if (!this.form) return;
      this.form.querySelectorAll('.filter-group').forEach((group) => {
        if (group.querySelector('.price-filter-container')) return;
        if (group.querySelector('.filter-options--category-links')) return;

        const options = [...group.querySelectorAll('.filter-option')];
        const visibleOptions = options.filter(
          (option) => !option.classList.contains('is-zero-count') && !option.hidden
        );
        const isEmpty = options.length === 0 || visibleOptions.length === 0;

        group.hidden = isEmpty;
        group.classList.toggle('is-empty-filter-group', isEmpty);
        group.setAttribute('aria-hidden', isEmpty ? 'true' : 'false');
      });
    }

    rebuildProductIndex() {
      if (this.catalogByHandle.size) {
        this.rebuildProductIndexFromCatalog();
        return;
      }
      this.productIndex = this.items.map((item) => this.finalizeCatalogProduct(getProductCardData(item)));
    }

    setGridLoading(isLoading) {
      if (!this.grid) return;
      this.grid.classList.toggle('basic-col-grid--loading', isLoading);
      this.grid.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    cacheCardHtmlFromString(html, wantedSet) {
      if (!html) return;
      const temp = document.createElement('div');
      temp.innerHTML = html;
      temp.querySelectorAll('li[data-product-handle]').forEach((li) => {
        const handle = li.getAttribute('data-product-handle');
        if (!handle) return;
        if (wantedSet && !wantedSet.has(handle)) return;
        if (!this.cardHtmlCache.has(handle)) {
          this.cardHtmlCache.set(handle, minifyCachedCardHtml(li.outerHTML));
        }
      });
    }

    async fetchCardsByHandles(handles) {
      const missing = [...new Set(handles.filter((handle) => handle && !this.cardHtmlCache.has(handle)))];
      if (!missing.length || !this.config || !this.config.sectionApiId) return;

      const batches = [];
      for (let i = 0; i < missing.length; i += FETCH_HANDLE_BATCH) {
        batches.push(missing.slice(i, i + FETCH_HANDLE_BATCH));
      }

      await runPool(batches, FETCH_CONCURRENCY, async (batch) => {
        if (!batch.length) return;
        const url = this.buildSectionApiUrl({
          basic_col_render: '1',
          filtered_handles: batch.join(','),
        });

        const res = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const rawHtml = data[this.config.sectionApiId] || Object.values(data)[0] || '';
        const html = stripCardHtml(rawHtml);
        if (html) {
          this.cacheCardHtmlFromString(html, new Set(batch));
        }
      });
    }

    async renderFilteredGrid(handles) {
      if (!this.grid) return;

      this.filtered = true;
      if (this.paginationEl) this.paginationEl.hidden = true;

      if (!this.gridItems.size) this.buildGridIndex();

      if (!handles.length) {
        this.gridItems.forEach((li) => this.setItemVisible(li, false));
        this.items = [];
        return;
      }

      this.setGridLoading(true);
      try {
        await this.fetchCardsByHandles(handles);

        const missing = handles.filter((handle) => handle && !this.gridItems.has(handle));
        if (missing.length) {
          const html = missing
            .map((handle) => this.cardHtmlCache.get(handle))
            .filter(Boolean)
            .join('');
          appendProductCards(this.grid, html, this.gridItems);
        }

        reorderGridToHandles(this.grid, handles, this.gridItems);

        const handleSet = new Set(handles);
        this.gridItems.forEach((li, handle) => {
          this.setItemVisible(li, handleSet.has(handle));
        });

        this.items = handles
          .map((handle) => this.gridItems.get(handle))
          .filter(Boolean);

        this.ensureCardStyles();
        this.syncGridUi();
      } finally {
        this.setGridLoading(false);
      }
    }

    restoreInitialGrid() {
      if (!this.grid) return;

      this.filtered = false;
      if (this.paginationEl) this.paginationEl.hidden = false;

      const parent = this.grid.parentElement;
      if (parent) {
        parent.querySelector('[data-basic-card-styles]')?.remove();
      }

      this.grid.innerHTML = this.initialGridHtml;
      this.items = [...this.grid.querySelectorAll('li[data-product-handle]')];
      this.buildGridIndex();
      this.cacheInitialCards();
      this.preservedCardStyles = this.extractCardStylesFromGrid();
      this.syncGridUi();
    }

    getActiveForDisplay() {
      const display = new Map();
      const active = this.getActive();
      Object.keys(active).forEach((key) => {
        if (active[key] && active[key].size) display.set(key, active[key]);
      });
      if (this.priceRange) {
        display.set('price', this.priceRange);
      }
      return display;
    }

    filterCategoryLabel(key) {
      const defaults = {
        vendor: 'Brand',
        product_type: 'Product Type',
        size: 'Size',
        color: 'Colour',
        availability: 'Availability',
        price: 'Price',
      };
      if (defaults[key]) return defaults[key];

      const group = this.form.querySelector(`[data-filter-group="${escapeSelector(key)}"]`);
      const titleTextEl = group?.closest('.filter-group')?.querySelector('.filter-group-title__text');
      if (titleTextEl) {
        return titleTextEl.textContent.trim();
      }

      const titleEl = group?.closest('.filter-group')?.querySelector('.filter-group-title');
      if (titleEl) {
        return titleEl.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      }
      return formatLabel(String(key).replace(/^tag_/, ''));
    }

    filterValueLabel(key, value) {
      if (key === 'availability') {
        if (value === 'in-stock') return 'In Stock';
        if (value === 'out-of-stock') return 'Out of Stock';
      }

      const input = this.form.querySelector(
        `input[name="${escapeSelector(key)}"][value="${escapeSelector(value)}"]`
      );
      const inputLabel = input?.closest('.filter-option')?.querySelector('.filter-label');
      if (inputLabel?.dataset.filterLabelBase) {
        return inputLabel.dataset.filterLabelBase.trim();
      }
      if (inputLabel?.textContent) {
        return inputLabel.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      }

      return formatLabel(value);
    }

    appendActiveFilterChip(categoryLabel, valueLabel, key, filterValue) {
      const list = this._activeFiltersList;
      if (!list) return;

      const item = document.createElement('li');
      item.className = 'nav-col-active-filters__item';

      const text = document.createElement('span');
      text.className = 'nav-col-active-filters__text';

      const category = document.createElement('span');
      category.className = 'nav-col-active-filters__category';
      category.textContent = `${categoryLabel}: `;

      const value = document.createElement('strong');
      value.className = 'nav-col-active-filters__value';
      value.textContent = valueLabel;

      text.appendChild(category);
      text.appendChild(value);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'nav-col-active-filters__remove';
      removeBtn.setAttribute('aria-label', `Remove ${categoryLabel}: ${valueLabel}`);
      removeBtn.addEventListener('click', () => {
        if (key === 'price') {
          this.priceRange = null;
          this.form.querySelectorAll('[data-basic-price-input]').forEach((input) => {
            input.value = '';
          });
        } else if (filterValue != null) {
          const input = this.form.querySelector(
            `input[name="${escapeSelector(key)}"][value="${escapeSelector(filterValue)}"]`
          );
          if (input) input.checked = false;
          this.syncTagBoxState();
        }
        void this.apply();
      });

      item.appendChild(text);
      item.appendChild(removeBtn);
      list.appendChild(item);
    }

    updateActiveFilterChips() {
      if (!this.tagsEl) return;

      this.tagsEl.innerHTML = '';
      this._activeFiltersList = null;
      const activeForDisplay = this.getActiveForDisplay();

      if (!activeForDisplay.size) {
        this.tagsEl.hidden = true;
        return;
      }

      this.tagsEl.hidden = false;

      const header = document.createElement('div');
      header.className = 'nav-col-active-filters__header';

      const title = document.createElement('p');
      title.className = 'nav-col-active-filters__title';
      title.textContent = 'Refine by';

      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'nav-col-active-filters__clear';
      clearBtn.textContent = 'Clear All';
      clearBtn.addEventListener('click', () => this.clear());

      header.appendChild(title);
      header.appendChild(clearBtn);
      this.tagsEl.appendChild(header);

      const list = document.createElement('ul');
      list.className = 'nav-col-active-filters__list';
      this.tagsEl.appendChild(list);
      this._activeFiltersList = list;

      activeForDisplay.forEach((values, key) => {
        if (key === 'price') {
          const { min, max } = values;
          let text = '';
          if (min > 0 && max < Infinity) text = `R ${min} - R ${max}`;
          else if (min > 0) text = `Over R ${min}`;
          else if (max < Infinity) text = `Under R ${max}`;
          if (text) this.appendActiveFilterChip(this.filterCategoryLabel(key), text, key, null);
          return;
        }

        Array.from(values).forEach((value) => {
          this.appendActiveFilterChip(
            this.filterCategoryLabel(key),
            this.filterValueLabel(key, value),
            key,
            value
          );
        });
      });
    }

    updateVisibleCount(visible) {
      const countText = visible === 1 ? '1 product' : visible + ' products';
      this.countEls.forEach((el) => {
        el.textContent = countText;
      });
      this.updateSearchResultsHeading(visible);
      if (this.emptyEl) {
        const showEmpty = visible === 0;
        this.emptyEl.hidden = !showEmpty;
        this.emptyEl.classList.toggle('is-visible', showEmpty);
      }
    }

    async apply() {
      if (this._applying) return;
      this._applying = true;

      try {
        await this.ensureCatalogLoaded();
        if (!this.productIndex.length) {
          this.rebuildProductIndex();
        }

        const active = this.getActive();
        const filtering = this.hasActiveFilters(active);

        if (!filtering) {
          this.restoreInitialGrid();
          const visible = this.items.length;
          this.updateVisibleCount(visible);
          this.syncFacetCounts();
          return;
        }

        const handles = this.getMatchingHandles(active);
        await this.renderFilteredGrid(handles);
        this.updateVisibleCount(handles.length);
        this.syncFacetCounts();
      } finally {
        this.updateActiveFilterChips();
        this._applying = false;
      }
    }

    clear() {
      this.form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.checked = false;
      });
      this.form.querySelectorAll('[data-basic-price-input]').forEach((input) => {
        input.value = '';
      });
      this.priceRange = null;
      this.syncTagBoxState();
      void this.apply();
    }
  }

  function initAll() {
    document.querySelectorAll('[data-basic-collection-filters]').forEach((root) => {
      if (root.dataset.basicFiltersInit === 'true') return;
      root.dataset.basicFiltersInit = 'true';
      new BasicCollectionFilters(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
